import React from 'react';
import { FormControl, FormHelperText, InputLabel, MenuItem, Select } from '@mui/material';
import Box from '@mui/material/Box';
import { Notice } from '@routes-plan/ui';
import { currentLang, translate, useT } from '../../../i18n';

export type PricingMode = 'CONSTANT' | 'PRODUCT';

export const PRICING_MODE_OPTIONS: { value: PricingMode; label: string; help: string }[] = [
  // Textos en el idioma actual cada vez que se leen.
  {
    value: 'CONSTANT',
    get label() {
      return translate(currentLang(), 'inventory.pricing.constant');
    },
    get help() {
      return translate(currentLang(), 'inventory.pricing.constantHelp');
    },
  },
  {
    value: 'PRODUCT',
    get label() {
      return translate(currentLang(), 'inventory.pricing.product');
    },
    get help() {
      return translate(currentLang(), 'inventory.pricing.productHelp');
    },
  },
];

type OfficeLike = { id: number | string; businessModel?: { allowProductLoans?: boolean } | null };

/** true si la oficina seleccionada presta producto (objeto real). */
export const officeAllowsProductLend = (offices: OfficeLike[] | undefined, officeId: unknown) =>
  Boolean(
    (offices || []).find((office) => String(office.id) === String(officeId))?.businessModel?.allowProductLoans,
  );

/**
 * "Cálculo del préstamo de producto" del cobro. Solo se muestra en cobros de
 * oficinas de objeto real; en las demás el cobro siempre usa constante.
 */
const PricingModeField: React.FC<{
  value: PricingMode;
  onChange: (value: PricingMode) => void;
  visible: boolean;
}> = ({ value, onChange, visible }) => {
  const t = useT();
  if (!visible) return null;
  const selected = PRICING_MODE_OPTIONS.find((option) => option.value === value) || PRICING_MODE_OPTIONS[0];
  return (
    <>
      <FormControl fullWidth size='small'>
        <InputLabel id='pricing-mode-label'>{t('inventory.pricing.label')}</InputLabel>
        <Select
          labelId='pricing-mode-label'
          label={t('inventory.pricing.label')}
          value={value}
          onChange={(event) => onChange(event.target.value as PricingMode)}
        >
          {PRICING_MODE_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
        <FormHelperText>{selected.help}</FormHelperText>
      </FormControl>
      {value === 'PRODUCT' && (
        <Box sx={{ mt: 1 }}>
          <Notice severity='info'>
          {t('inventory.pricing.productInfo')}
          </Notice>
        </Box>
      )}
    </>
  );
};

export default PricingModeField;
